import { useEffect, useMemo, useState } from 'react';
import { useAppAuth } from '../components/AuthContext.jsx';
import { listMaklers } from '../services/maklerLevels.js';
import { listJchOverview } from '../services/jchDashboard.js';
import { useNavigate } from 'react-router-dom';
import { usePolicy, levelFor } from '../components/PolicyContext.jsx';

const SEMAFOR_MAP = {
  green: 'Zelená',
  Green: 'Zelená',
  Zelena: 'Zelená',
  orange: 'Oranžová',
  Orange: 'Oranžová',
  Oranzova: 'Oranžová',
  red: 'Červená',
  Red: 'Červená',
  Cervena: 'Červená',
};

function normalizeSemafor(value) {
  if (!value) return '—';
  return SEMAFOR_MAP[value] || value;
}

function semaforClass(value) {
  const normalized = (value || '').toString().toLowerCase();
  if (normalized === 'green' || normalized.includes('zel')) return 'semafor semafor--grn';
  if (normalized.includes('ora')) return 'semafor semafor--org';
  if (normalized.includes('red') || normalized.includes('cerv')) return 'semafor semafor--red';
  return 'semafor semafor--none';
}

export default function Database() {
  const { policy } = usePolicy();
  const { user } = useAppAuth();
  const navigate = useNavigate();
  const [actor, setActor] = useState(user?.role === 'JCH' ? 'JCH' : 'JR');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role === 'JCH') setActor('JCH');
    if (user?.role === 'JR') setActor('JR');
  }, [user]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = actor === 'JCH' ? await listJchOverview() : await listMaklers();
        if (!active) return;
        setRows((data && data.maklers) || data || []);
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Databázi se nepodařilo načíst.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [actor]);

  const summary = useMemo(() => {
    const total = rows.length;
    const perLevel = Object.fromEntries(policy.levels.map((l) => [l.name, 0]));
    const semafor = { Zelená: 0, Oranžová: 0, Červená: 0, '—': 0 };

    rows.forEach((row) => {
      const levelKey = levelFor(policy, row)?.name ?? row.level_name ?? '—';
      if (perLevel[levelKey] !== undefined) perLevel[levelKey] += 1;

      const s = normalizeSemafor(row.semafor);
      if (semafor[s] !== undefined) semafor[s] += 1;
      else semafor['—'] += 1;
    });

    return { total, perLevel, semafor };
  }, [rows, policy]);

  const isAdminView = actor === 'JCH';

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">Databáze makléřů</h1>
          <p className="dashboard__lead">
            {isAdminView
              ? 'JCH view: přehled skupiny a semafor jednotlivých makléřů v reálném čase.'
              : 'JR view: databázový přehled makléřů a jejich aktuální plán/inkrement.'}
          </p>
        </div>

        <div className="dashboard__status" style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className={actor === 'JR' ? 'button button--spot' : 'button button--secondary'}
            onClick={() => setActor('JR')}
          >
            JR
          </button>
          <button
            type="button"
            className={actor === 'JCH' ? 'button button--spot' : 'button button--secondary'}
            onClick={() => setActor('JCH')}
          >
            JCH
          </button>
        </div>
      </div>

      {error && (
        <p className="dashboard__error" role="alert">
          {error}
        </p>
      )}

      <div className="dashboard__cards">
        <article className="card">
          <span className="card__caption">Celkem</span>
          <span className="card__big">{summary.total}</span>
          <span className="card__hint">{isAdminView ? 'Makléři v JCH přehledu' : 'Makléři v JR přehledu'}</span>
        </article>

        <article className="card">
          <span className="card__caption">Úrovně</span>
          <span className="card__big">{policy.levels.slice(1).reduce((n, l) => n + summary.perLevel[l.name], 0)}</span>
          <span className="card__hint">
            {policy.levels.map((l) => `${summary.perLevel[l.name]} ${l.name}`).join(' · ')}
          </span>
        </article>

        <article className="card">
          <span className="card__caption">Semafor</span>
          <span className="card__big">{summary.semafor.Zelená + summary.semafor.Oranžová + summary.semafor.Červená}</span>
          <span className="card__hint">
            {summary.semafor.Zelená} zelených · {summary.semafor.Oranžová} oranžových · {summary.semafor.Červená} červených
          </span>
        </article>
      </div>

      <div className="overview" role="table" aria-label="Databáze makléřů">
        <div className="overview__row overview__row--head">
          <span className="overview__head">Makléř</span>
          <span className="overview__head">Fáze</span>
          <span className="overview__head">Úroveň</span>
          <span className="overview__head">Semafor</span>
          <span className="overview__head">Status</span>
          <span className="overview__head">Plán</span>
        </div>

        {loading ? (
          <div className="overview__row overview__row--empty">
            <span className="dashboard__status">Načítá se…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="overview__row overview__row--empty">
            <span className="dashboard__status">Žádní makléři k zobrazení.</span>
          </div>
        ) : (
          rows.map((row) => {
            const level = levelFor(policy, row);
            const levelName = level?.name ?? row.level_name ?? '—';
            const plan = row.plan || null;
            const semaforValue = normalizeSemafor(row.semafor);

            return (
              <div
                key={row.id}
                className="overview__row"
                onClick={() => navigate(`/dashboard/database/${row.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <span className="overview__cell overview__cell--name">
                  {row.name || row.full_name || '—'}
                </span>
                <span className="overview__cell">
                  {row.phase || (row.is_newcomer ? 'Příprava' : levelName)}
                </span>
                <span className="overview__cell" style={{ color: level?.color }}>{levelName}</span>
                <span className="overview__cell">
                  <span className={semaforClass(row.semafor)}>{semaforValue}</span>
                </span>
                <span className="overview__cell">{row.status || 'active'}</span>
                <span className="overview__cell">
                  {plan ? `${plan.contacts_completed ?? 0} / ${plan.target_contacts ?? '?'} · ${plan.plan_completion_pct ?? '?'}%` : '—'}
                </span>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
